/**
 * Pure gesture recognizer.
 *
 * No DOM event dependency: driven by `start/move/end` coordinate calls so it is
 * trivially unit-testable and reusable across input bindings. Owns:
 *   - the 8px deadzone before engaging,
 *   - axis-lock (and aborting a single-axis gesture on a perpendicular drag, so
 *     it never fights native scroll),
 *   - the `canEngage` / `canStart` gates,
 *   - fling detection,
 *   - rising-edge haptic at the commit threshold.
 */

import { vibrate } from "./haptics";
import type { Axis, Direction, GestureConfig, GestureRecognizer, GestureSample } from "./types";

const DEFAULT_THRESHOLD_PX = 8;
const DEFAULT_HAPTIC_MS = 15;

function directionFor(axis: Axis, dx: number, dy: number): Direction | null {
  if (axis === "x") {
    if (dx > 0) {
      return "right";
    }
    return dx < 0 ? "left" : null;
  }
  if (dy > 0) {
    return "down";
  }
  return dy < 0 ? "up" : null;
}

function isFling(sample: GestureSample, config: GestureConfig): boolean {
  const { fling } = config;
  if (!fling || sample.axis === null) {
    return false;
  }
  const axisDelta = sample.axis === "x" ? sample.dx : sample.dy;
  const perpDelta = sample.axis === "x" ? sample.dy : sample.dx;
  return (
    sample.duration < fling.maxDurationMs &&
    Math.abs(axisDelta) > fling.minDistancePx &&
    Math.abs(axisDelta) > Math.abs(perpDelta) * fling.axisRatio
  );
}

function resolveCommitPx(config: GestureConfig, axisSize: number): number {
  const { commitPx } = config;
  if (commitPx === undefined) {
    return Number.POSITIVE_INFINITY;
  }
  return typeof commitPx === "function" ? commitPx(axisSize) : commitPx;
}

export function createGestureRecognizer(config: GestureConfig): GestureRecognizer {
  const allowedAxis = config.axis ?? "both";
  const threshold = config.thresholdPx ?? DEFAULT_THRESHOLD_PX;
  const hapticMs = config.hapticMs ?? DEFAULT_HAPTIC_MS;

  let tracking = false;
  let engaged = false;
  let axis: Axis | null = null;
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let crossedCommit = false;

  const buildSample = (x: number, y: number): GestureSample => {
    const dx = x - startX;
    const dy = y - startY;
    const lockedAxis = axis;
    const distance = lockedAxis === null ? 0 : Math.abs(lockedAxis === "x" ? dx : dy);
    const duration = Date.now() - startTime;
    let axisDelta = 0;
    if (lockedAxis === "x") {
      axisDelta = dx;
    } else if (lockedAxis === "y") {
      axisDelta = dy;
    }
    const sample: GestureSample = {
      dx,
      dy,
      axis: lockedAxis,
      direction: lockedAxis === null ? null : directionFor(lockedAxis, dx, dy),
      distance,
      duration,
      velocity: duration > 0 ? axisDelta / duration : 0,
      isFling: false,
    };
    sample.isFling = isFling(sample, config);
    return sample;
  };

  const reset = () => {
    tracking = false;
    engaged = false;
    axis = null;
    crossedCommit = false;
  };

  const maybeHaptic = (sample: GestureSample) => {
    if (!config.haptics || config.commitPx === undefined) {
      return;
    }
    const threshPx = resolveCommitPx(config, 0);
    const crossed = sample.distance > threshPx;
    if (crossed && !crossedCommit) {
      crossedCommit = true;
      vibrate(hapticMs);
    } else if (!crossed && crossedCommit) {
      crossedCommit = false;
    }
  };

  return {
    start(x, y, info) {
      if (config.canStart && !config.canStart({ target: info?.target ?? null })) {
        return;
      }
      startX = x;
      startY = y;
      startTime = Date.now();
      tracking = true;
      engaged = false;
      axis = null;
      crossedCommit = false;
    },

    move(x, y, preventDefault) {
      if (!tracking) {
        return;
      }
      const dx = x - startX;
      const dy = y - startY;

      if (!engaged) {
        if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) {
          return;
        }
        const candidate: Axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (allowedAxis !== "both" && allowedAxis !== candidate) {
          // Perpendicular drag on a single-axis gesture — yield to native scroll.
          tracking = false;
          return;
        }
        axis = candidate;
        const sample = buildSample(x, y);
        if (config.canEngage && !config.canEngage(candidate, sample)) {
          tracking = false;
          axis = null;
          return;
        }
        engaged = true;
        config.onStart?.(sample);
        return;
      }

      preventDefault?.();
      const sample = buildSample(x, y);
      maybeHaptic(sample);
      config.onMove?.(sample);
    },

    end(x, y) {
      if (!tracking) {
        return;
      }
      const wasEngaged = engaged;
      const sample = wasEngaged ? buildSample(x, y) : null;
      reset();
      if (sample) {
        config.onEnd?.(sample);
      }
    },

    cancel() {
      const wasEngaged = engaged;
      reset();
      if (wasEngaged) {
        config.onCancel?.();
      }
    },

    isEngaged: () => engaged,
    isTracking: () => tracking,
  };
}
